import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Rocket, KeyRound, User as UserIcon } from "lucide-react";
import { toast } from "sonner";

export default function AuthPage() {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [callSign, setCallSign] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const from = loc.state?.from?.pathname || "/pilot";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await login(email, password);
        toast.success("HANDSHAKE COMPLETE");
      } else {
        await register(email, password, callSign);
        toast.success("PILOT ENLISTED");
      }
      nav(from, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "AUTHENTICATION FAILED");
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-md mx-auto py-10" data-testid="page-auth">
      <div className="text-center mb-8">
        <div className="inline-block w-16 h-16 border-2 border-[color:var(--sat-cyan)] sat-box-glow relative mb-4 sat-brackets">
          <div className="absolute inset-3 border border-[color:var(--sat-cyan)] flex items-center justify-center">
            <Rocket size={20} className="sat-cyan" />
          </div>
        </div>
        <div className="sat-label sat-cyan">ASTRONATLAS · GROUND CONTROL</div>
        <h1 className="font-display font-bold text-3xl md:text-4xl mt-2">
          {mode === "login" ? "PILOT SIGN-IN" : "ENLIST PILOT"}
        </h1>
      </div>

      <div className="flex gap-2 mb-6">
        <button data-testid="auth-mode-login" onClick={() => setMode("login")}
          className={`sat-btn flex-1 justify-center ${mode === "login" ? "sat-btn-solid" : ""}`}>SIGN-IN</button>
        <button data-testid="auth-mode-register" onClick={() => setMode("register")}
          className={`sat-btn flex-1 justify-center ${mode === "register" ? "sat-btn-solid" : ""}`}>ENLIST</button>
      </div>

      <form onSubmit={submit} className="sat-surface sat-brackets p-6 flex flex-col gap-4">
        {mode === "register" && (
          <div>
            <label className="sat-label mb-2 flex items-center gap-2"><UserIcon size={11}/> CALL SIGN (optional)</label>
            <input type="text" value={callSign} onChange={(e) => setCallSign(e.target.value)}
              placeholder="e.g. STARFARER" data-testid="auth-callsign" />
          </div>
        )}
        <div>
          <label className="sat-label mb-2 block">TRANSMISSION EMAIL</label>
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="pilot@astronatlas.io" data-testid="auth-email" />
        </div>
        <div>
          <label className="sat-label mb-2 flex items-center gap-2"><KeyRound size={11}/> PASSCODE</label>
          <input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder="MIN. 6 CHARACTERS" data-testid="auth-password" />
        </div>

        <button type="submit" disabled={loading} className="sat-btn sat-btn-solid justify-center !py-3 mt-2" data-testid="auth-submit">
          {loading ? "TRANSMITTING…" : mode === "login" ? "INITIATE HANDSHAKE ▸" : "ENLIST ▸"}
        </button>

        <div className="sat-label text-center pt-2">
          {mode === "login" ? (
            <>NEW PILOT? <button type="button" onClick={() => setMode("register")} className="sat-cyan hover:underline" data-testid="switch-to-register">ENLIST HERE</button></>
          ) : (
            <>ACTIVE PILOT? <button type="button" onClick={() => setMode("login")} className="sat-cyan hover:underline" data-testid="switch-to-login">SIGN IN</button></>
          )}
        </div>
      </form>

      <div className="text-center mt-6">
        <Link to="/news" className="sat-label hover:sat-cyan" data-testid="auth-skip">← CONTINUE AS GUEST</Link>
      </div>
    </div>
  );
}
